<template>
  <div class="activity">
    <div class="activity-info">
      <div class="expansion-controls">
        <div
          class="btn-expand-all"
          @click.stop="onExpandAll"
          :class="{
            disabled: expandedSize === data.length || data.length === 0
          }"
        >
          Expand all <span>prefixes</span>
        </div>
        <div
          class="btn-collapse-all"
          @click.stop="onCollapseAll"
          :class="{ disabled: expandedSize === 0 || data.length === 0 }"
        >
          Collapse all <span>prefixes</span>
        </div>
      </div>
      <div class="activity-row">
        <div class="activity-page-size">
          <div class="activity-page-size__text">Show</div>
          <div>
            <select v-model="pageSize">
              <option value="10">10 prefixes</option>
              <option value="30">30 prefixes</option>
              <option value="50">50 prefixes</option>
              <option value="100">100 prefixes</option>
            </select>
          </div>
        </div>
        <div class="mobile-only">
          <div @click="downloadData()" v-if="!selectable" id="v-step-6--mobile">
            <i class="icon-download" aria-hidden="true"></i>
          </div>
          <i class="icon-search" @click="showMobileSearch = true"></i>
          <Input
            class="search-bar search-bar--mobile"
            :placeholder="`Type a prefix`"
            v-model="filterText"
            v-if="showMobileSearch"
            v-click-outside="
              () => {
                if (showMobileSearch) showMobileSearch = false;
              }
            "
          >
            <div slot="append">
              <i class="icon-search" @click="showMobileSearch = false"></i>
            </div>
          </Input>
        </div>
        <Input
          class="search-bar search-bar--full"
          :placeholder="`Type a prefix`"
          v-model="filterText"
        >
          <div slot="append">
            <i class="icon-search"></i>
          </div>
        </Input>
      </div>
    </div>

    <div class="activity-table card">
      <div
        @click="downloadData()"
        class="download-button"
        v-if="!selectable"
        id="v-step-6"
        v-tooltip.left="{
          content: 'Download CSV file',
          classes: 'download-tooltip'
        }"
      >
        <i class="icon-download" aria-hidden="true"></i>
      </div>
      <DataTable
        :data="displayData"
        :fullData="data"
        :class="noAccount ? 'blur' : ''"
        noDataText="Our honeynet sensors have not seen any suspicious traffic coming from your AS for your selected time frame"
        :selectable="selectable"
        :expandAll="expandAll"
        @onExpandChange="onExpandChange"
      ></DataTable>
    </div>
    <div class="activity-pagination" v-if="data.length">
      <div class="pagination-left">
        <div>{{ entryText }}</div>
      </div>
      <nav class="pagination-nav">
        <div class="navigation-pane">
          <ul class="pagination mb-0">
            <li :class="{ 'page-item': true, disabled: currentPage <= 1 }">
              <a
                @click="setPage($event, currentPage - 1)"
                :class="{ 'page-link': true, cursor: currentPage > 1 }"
                >&laquo;</a
              >
            </li>
            <li
              v-for="page of displayPages"
              class="page-item page"
              :class="{
                active: page === currentPage,
                disabled: noAccount
              }"
              :key="page"
            >
              <a
                v-bind:class="{
                  'page-link': true,
                  cursor: currentPage !== page
                }"
                @click="setPage($event, page)"
                >{{ page }}</a
              >
            </li>
            <li
              :class="{
                'page-item': true,
                disabled: currentPage >= totalPages
              }"
            >
              <a
                @click="setPage($event, currentPage + 1)"
                v-bind:class="{
                  'page-link': true,
                  cursor: currentPage < totalPages
                }"
                >&raquo;</a
              >
            </li>
          </ul>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DataTable from './DataTable';
import { saveAs } from 'file-saver';
import fakeData from '../constants/fakePrefix';
import mixin from '../utils/mixin';
import * as vClickOutside from 'v-click-outside';

export default {
  mixins: [mixin],
  props: {
    selectable: { type: Boolean, default: false }
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 2,
      pageSize: 10,
      filterText: '',
      showMobileSearch: false,
      expandAll: true,
      expandedSize: 0
    };
  },
  methods: {
    setPage(event, page) {
      event.stopPropagation();
      if (page < 1) {
        this.currentPage = 1;
      } else if (page > this.totalPages) {
        this.currentPage = this.totalPages;
      } else {
        this.currentPage = page;
      }
    },
    downloadData() {
      let data = 'prefix,ip,protocol,destination port,number of hits\n';
      this.data.forEach(i => {
        if (i.sourceIP) {
          i.sourceIP.forEach(j => {
            data += `${i.prefix},${j.ip},${j.protocol},${j.port},${j.count}\n`;
          });
        }
      });
      const blob = new Blob([data], { type: 'text/csv' });
      saveAs(blob, 'top offending prefixes.csv');
    },
    onExpandAll() {
      this.expandAll = true;
    },
    onCollapseAll() {
      this.expandAll = false;
    },
    onExpandChange(e) {
      this.expandedSize = e;
      this.expandAll = null;
    }
  },
  watch: {
    pageSize() {
      this.totalPages = Math.ceil(this.data.length / this.pageSize);
      this.currentPage = 1;
    },
    selectedASN() {
      this.filterText = '';
    }
  },
  computed: {
    ...mapGetters('dataStore', ['noAccount', 'prefixDataArray']),
    data() {
      let data = this.prefixDataArray;

      return data;
    },
    filteredData() {
      if (this.noAccount) {
        return fakeData;
      }
      let data = JSON.parse(JSON.stringify(this.data));
      if (this.filterText !== '') {
        this.currentPage = 1;
        data = data.filter(i => i.prefix.indexOf(this.filterText) !== -1);
      }
      return data;
    },
    displayData() {
      this.totalPages = Math.ceil(this.filteredData.length / this.pageSize);
      const start = (this.currentPage - 1) * this.pageSize;
      const end = this.currentPage * this.pageSize;
      return this.filteredData.slice(start, end);
    },
    displayPages() {
      let start = this.currentPage;
      let end = start + 5;
      if (end > this.totalPages) {
        end = this.totalPages;
        start = end - 5;
      }
      if (start < 1) {
        start = 1;
      }
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    entryText() {
      const start = (this.currentPage - 1) * this.pageSize + 1;
      let end = this.currentPage * this.pageSize;
      if (end > this.filteredData.length) {
        end = this.filteredData.length;
      }
      return `Showing ${start} to ${end} of ${
        this.filteredData.length
      } entries`;
    }
  },
  components: {
    DataTable
  }
};
</script>

<style lang="scss">
.activity {
  .activity-info {
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    .expansion-controls {
      display: flex;
      min-width: 360px;

      & > div {
        background: #f8f8f9;
        padding: 0 8px;
        line-height: 28px;
        margin-bottom: 4px;
        cursor: pointer;

        &.disabled {
          color: #ccc;
          pointer-events: none;
        }

        &:first-child {
          margin-right: 8px;
        }
      }

      @media screen and (max-width: 480px) {
        min-width: 270px;

        span {
          display: none;
        }
      }
    }
  }

  .activity-row {
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
    align-items: center;
    height: 40px;
    margin-bottom: 4px;
  }

  .download-button {
    position: absolute;
    right: 10px;
    top: 10px;
    cursor: pointer;

    i {
      font-size: 20px;
    }

    @media screen and (max-width: 485px) {
      display: none;
    }
  }

  .activity-page-size {
    display: flex;

    &__text {
      background: #f8f8f9;
      width: 100px;
      text-align: center;
      line-height: 28px;
    }

    select {
      border: 2px solid #f8f8f9;
      background: url(data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgZGF0YS1uYW1lPSJMYXllciAxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0Ljk1IDEwIj48ZGVmcz48c3R5bGU+LmNscy0xe2ZpbGw6I2ZmZjt9LmNscy0ye2ZpbGw6IzQ0NDt9PC9zdHlsZT48L2RlZnM+PHRpdGxlPmFycm93czwvdGl0bGU+PHJlY3QgY2xhc3M9ImNscy0xIiB3aWR0aD0iNC45NSIgaGVpZ2h0PSIxMCIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIxLjQxIDQuNjcgMi40OCAzLjE4IDMuNTQgNC42NyAxLjQxIDQuNjciLz48cG9seWdvbiBjbGFzcz0iY2xzLTIiIHBvaW50cz0iMy41NCA1LjMzIDIuNDggNi44MiAxLjQxIDUuMzMgMy41NCA1LjMzIi8+PC9zdmc+)
        no-repeat 100% 50%;
      appearance: none;
      outline-width: 0;
      width: 110px;
      padding-right: 10px;
    }
  }
  .activity-pagination {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;

    @media screen and (max-width: 485px) {
      justify-content: flex-end;
    }

    .pagination-left {
      display: flex;
      width: 300px;
      justify-content: space-between;

      @media screen and (max-width: 485px) {
        display: none;
      }
    }
  }
  .activity-table {
    min-height: 520px;

    .table.blur {
      /deep/ .table__body {
        filter: blur(4px);
        pointer-events: none;
      }
    }
  }
  .pagination-nav {
    display: flex;
    flex-direction: row;

    .navigation-pane {
      display: flex;
      flex-direction: row;
    }
    .pagination {
      height: 36px;

      .page {
        @media screen and (max-width: 485px) {
          display: none;
        }
      }

      li {
        &.active {
          color: #fff !important;

          .page-link {
            border: none;
          }
        }

        &.disabled {
          cursor: not-allowed;
        }
      }

      .page-link {
        border: 1px solid #efefef;
        height: 36px;
      }
    }
  }
  .mobile-only {
    display: none;

    @media screen and (max-width: 485px) {
      display: flex;
      position: relative;

      i {
        margin: 0 5px;
        font-size: 18px;
      }
    }
  }
  .search-bar {
    max-width: 276px;
    border-radius: 0px;

    .ivu-input-group-append {
      padding: 0;
      border: none;
    }

    i.icon-search {
      font-size: 24px;
      display: flex;
      align-items: center;
      height: 38px;
      width: 41px;
      justify-content: center;
      background-color: #808080;
      color: white;
      border: 1px solid #979797;
      border-left: none;
      margin: 0;
    }

    input {
      height: 38px;
      font-size: 16px;
      border: 1px solid #979797;
      outline: none;
      box-shadow: none;
      border-radius: 0px;
    }

    &--full {
      @media screen and (max-width: 485px) {
        display: none;
      }
    }

    &--mobile {
      position: absolute;
      right: 0;
      width: calc(100vw - 50px);
      height: 34px;

      input {
        height: 34px;
      }

      i.icon-search {
        height: 34px;
        font-size: 18px;
        width: 38px;
      }
    }
  }
  .page-item.disabled .page-link {
    color: #cccccc;
    border-color: #cccccc;
  }
}
</style>
